/**
 * use-company-details.ts
 *
 * React hook that wraps companyService.ts and exposes a clean API
 * for reading and mutating the current company's invoicing details.
 *
 * Exposed API (per PRD §1.11b):
 *  - details           — current (possibly unsaved) CompanyDetails state
 *  - saveDetails()     — persist staged changes via companyService, then push to CompanyContext
 *  - discardChanges()  — reset staged state back to last-saved values
 *  - isSaving          — true while the async save is in flight
 *  - hasUnsavedChanges — true when staged state differs from last-saved values
 *
 * Additionally exposes lower-level setters used by the form UI:
 *  - setField()        — update a single top-level field
 *  - setAddressField() — update a single registeredAddress sub-field
 *  - setBankField()    — update a single bankDetails sub-field
 *  - setLogoUrl()      — update the logoUrl field directly
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCompanyDetails,
  updateCompanyDetails,
  mapToCompanyFields,
  type CompanyDetails,
} from '@/services/companyService';
import { useCompany } from '@/contexts/CompanyContext';
import type { Address } from '@/types/index';
import type { BankDetails } from '@/types/company';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseCompanyDetailsReturn {
  /** Current (possibly unsaved) form state */
  details: CompanyDetails;
  /** Persist staged changes to storage and push into CompanyContext */
  saveDetails: () => Promise<void>;
  /** Reset form to last-saved values */
  discardChanges: () => void;
  /** True while saveDetails() is awaiting the service call */
  isSaving: boolean;
  /** True when the staged form state differs from last-saved values */
  hasUnsavedChanges: boolean;
  // Convenience setters
  setField: <K extends keyof CompanyDetails>(key: K, value: CompanyDetails[K]) => void;
  setAddressField: <K extends keyof Address>(key: K, value: Address[K]) => void;
  setBankField: <K extends keyof BankDetails>(key: K, value: BankDetails[K]) => void;
  setLogoUrl: (url: string | undefined) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCompanyDetails(): UseCompanyDetailsReturn {
  const { updateCurrentCompany } = useCompany();

  // last-saved snapshot — never mutated directly
  const savedRef = useRef<CompanyDetails | null>(null);

  // staged (form) state
  const [details, setDetails] = useState<CompanyDetails>({
    name: '',
    companyRegistrationNumber: '',
    vatNumber: '',
    phone: '',
    email: '',
    website: '',
    logoUrl: undefined,
    registeredAddress: {
      line1: '',
      line2: '',
      city: '',
      county: '',
      postcode: '',
      country: 'United Kingdom',
    },
    bankDetails: {
      bankName: '',
      accountName: '',
      sortCode: '',
      accountNumber: '',
      iban: '',
      swiftBic: '',
    },
  });

  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // ------------------------------------------------------------------
  // Load on mount
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    getCompanyDetails().then((loaded) => {
      if (cancelled) return;
      savedRef.current = loaded;
      setDetails(loaded);
      setHasUnsavedChanges(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // ------------------------------------------------------------------
  // Mark dirty whenever details diverge from the saved snapshot
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!savedRef.current) return;
    const dirty = JSON.stringify(details) !== JSON.stringify(savedRef.current);
    setHasUnsavedChanges(dirty);
  }, [details]);

  // ------------------------------------------------------------------
  // Save
  // ------------------------------------------------------------------
  const saveDetails = useCallback(async () => {
    setIsSaving(true);
    try {
      const saved = await updateCompanyDetails(details);
      savedRef.current = saved;
      setDetails(saved);
      setHasUnsavedChanges(false);
      // Push relevant fields into CompanyContext so the rest of the app
      // immediately reflects the updated company name, logo, etc.
      updateCurrentCompany(mapToCompanyFields(saved));
    } finally {
      setIsSaving(false);
    }
  }, [details, updateCurrentCompany]);

  // ------------------------------------------------------------------
  // Discard
  // ------------------------------------------------------------------
  const discardChanges = useCallback(() => {
    if (savedRef.current) {
      setDetails(savedRef.current);
      setHasUnsavedChanges(false);
    }
  }, []);

  // ------------------------------------------------------------------
  // Convenience setters
  // ------------------------------------------------------------------
  const setField = useCallback(
    <K extends keyof CompanyDetails>(key: K, value: CompanyDetails[K]) => {
      setDetails((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const setAddressField = useCallback(<K extends keyof Address>(key: K, value: Address[K]) => {
    setDetails((prev) => ({
      ...prev,
      registeredAddress: {
        ...(prev.registeredAddress ?? {}),
        [key]: value,
      } as Address,
    }));
  }, []);

  const setBankField = useCallback(<K extends keyof BankDetails>(key: K, value: BankDetails[K]) => {
    setDetails((prev) => ({
      ...prev,
      bankDetails: {
        ...(prev.bankDetails ?? {}),
        [key]: value,
      } as BankDetails,
    }));
  }, []);

  const setLogoUrl = useCallback((url: string | undefined) => {
    setDetails((prev) => ({ ...prev, logoUrl: url }));
  }, []);

  return {
    details,
    saveDetails,
    discardChanges,
    isSaving,
    hasUnsavedChanges,
    setField,
    setAddressField,
    setBankField,
    setLogoUrl,
  };
}
